import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch, batch } from 'react-redux';
import {
  Route, Switch, Redirect, useHistory, useLocation,
} from 'react-router-dom';
import Helmet from 'react-helmet';
import { useTheme } from 'emotion-theming';
import styled from '@emotion/styled';

import Layout from '../../shared/components/Layout/Layout';
import Button from '../../shared/_TBComponents/Button';
import Icon from '../../shared/_TBComponents/Icon';
import Text from '../../shared/_TBComponents/Text';
import hexToRgba from '../../shared/utils/hexToRgba';
import AthleteNavigator from './components/AthleteNavigator';
import CalendarNavigator from './components/CalendarNavigator';
import ManageAthletes from './components/ManageAthletes';
import ProgramNavigator from './components/ProgramNavigator';
import EditAthleteModal from './components/presentational/EditAthleteModal';
import PreviewCarousel from './components/presentational/PreviewCarousel';
import useSessionState from '../calendar/components/presentational/hooks/useSessionState';
import {
  fetchAthletes, fetchCalendars, fetchGroups, fetchPrograms,
} from './ducks/navigatorActions';

const Container = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  padding-top: 49px;
  background: #fff;
`;

const NavSection = styled.div`
  display: flex;
  flex-direction: column;
  width: ${(props) => (props.isNarrow ? '150px' : '260px')};
  background: #fff;
  border-right: 1px solid #e0e0e0;
  padding: 16px ${(props) => (props.isNarrow ? '8px' : '16px')};
  gap: 16px;

  @media screen and (max-width: 767px) {
    display: none;
  }
`;

const MobileHomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;
`;

const TabButtonWrapper = styled.div``;

const ContentSection = styled.div`
  flex: 1;
  overflow: auto;
`;

const MobileTabCard = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px;
  border: 1px solid #EEE;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  text-align: left;
  box-shadow: 0 4px 8px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
`;

const MobileTabCardCenter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  gap: 6px;
  text-align: center;
  padding: 0 18px;
`;

const MobileTabArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const TAB_CONFIG = [
  {
    key: 'athletes',
    name: 'Athletes',
    subtitle: 'Workout assignments, preview, and results.',
    icon: 'Users',

    desktopHeight: '110px',
  },
  {
    key: 'calendars',
    name: 'Calendars',
    subtitle: 'Scheduled workouts',
    icon: 'calendar2',

    desktopHeight: '90px',
  },
  {
    key: 'programs',
    name: 'Programs',
    subtitle: 'Self-paced workouts',
    icon: 'templates',

    desktopHeight: '90px',
  },
];

const Navigator = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const athlete = useSelector((state) => state.navigator.data.selectedUser);
  const calendars = useSelector((state) => state.navigator.data.calendars);
  const programs = useSelector((state) => state.navigator.data.programs);
  const groups = useSelector((state) => state.navigator.data.groups);
  const accountCode = currentUser?.accountCode;
  const userSettings = currentUser?.userSettings;
  const manageUsers = !!userSettings?.manageUsers;
  const accountPrimaryColor = currentUser?.id
    ? (theme?.colorPrimary || currentUser?.userSkin?.primary || '#FF6600')
    : undefined;
  const accountPrimaryBg = accountPrimaryColor ? hexToRgba(accountPrimaryColor, 0.05) : 'transparent';

  const [isNarrowScreen, setIsNarrowScreen] = React.useState(
    window.matchMedia('(max-width: 992px)').matches,
  );

  const [isMobile, setIsMobile] = React.useState(
    window.matchMedia('(max-width: 767px)').matches,
  );

  const [isEditAthleteModalOpen, setIsEditAthleteModalOpen] = useState(false);
  const [initialTab, setInitialTab] = useState('profile');
  const [addProgramMode, setAddProgramMode] = useState(false);

  // Persist last visited paths in session storage
  const [lastPath, setLastPath] = useSessionState(
    'dashboard',
    'lastPath',
    '/dashboard/athletes',
  );

  const [lastAthletesPath, setLastAthletesPath] = useSessionState(
    'dashboard',
    'lastPath:athletes',
    '/dashboard/athletes',
  );

  const [lastCalendarsPath, setLastCalendarsPath] = useSessionState(
    'dashboard',
    'lastPath:calendars',
    '/dashboard/calendars',
  );

  const [lastProgramsPath, setLastProgramsPath] = useSessionState(
    'dashboard',
    'lastPath:programs',
    '/dashboard/programs',
  );

  const openEditAthleteModal = (tab = 'profile', addMode = false) => {
    setInitialTab(tab);
    setAddProgramMode(addMode);
    setIsEditAthleteModalOpen(true);
  };

  const closeEditAthleteModal = () => {
    setIsEditAthleteModalOpen(false);
    setAddProgramMode(false);
  };

  // Activity update for PHP
  useEffect(() => {
    if (currentUser?.id) {
      const phpAppUrl = process.env.PHP_APP_URL
        || `https://${
          process.env.ENVIRONMENT === 'PRODUCTION' ? '' : 'staging.'
        }app.teambuildr.com`;

      fetch(`${phpAppUrl}/assets/activity_update.php`, {
        method: 'POST',
        credentials: 'include',
      }).catch(() => {
        // Silently fail
      });
    }
  }, [currentUser?.id]);

  // Update last visited paths
  useEffect(() => {
    if (!location.pathname.startsWith('/dashboard')) return;
    if (location.pathname === '/dashboard') return;
    if (location.pathname === '/dashboard/manage-athletes') return;

    const fullPath = `${location.pathname}${location.search || ''}`;
    setLastPath(fullPath);

    if (location.pathname.includes('/dashboard/calendars')) {
      setLastCalendarsPath(fullPath);
    } else if (location.pathname.includes('/dashboard/programs')) {
      setLastProgramsPath(fullPath);
    } else if (location.pathname.includes('/dashboard/athletes')) {
      setLastAthletesPath(fullPath);
    }
  }, [location.pathname, location.search]);

  // Guard against invalid paths
  useEffect(() => {
    if (lastAthletesPath && !lastAthletesPath.includes('/dashboard/athletes')) {
      setLastAthletesPath('/dashboard/athletes');
    }
    if (lastCalendarsPath && !lastCalendarsPath.includes('/dashboard/calendars')) {
      setLastCalendarsPath('/dashboard/calendars');
    }
    if (lastProgramsPath && !lastProgramsPath.includes('/dashboard/programs')) {
      setLastProgramsPath('/dashboard/programs');
    }
  }, [lastAthletesPath, lastCalendarsPath, lastProgramsPath]);

  useEffect(() => {
    if (accountCode) {
      batch(() => {
        dispatch(fetchAthletes(accountCode));
        dispatch(fetchCalendars(accountCode));
        dispatch(fetchGroups(accountCode));
        dispatch(fetchPrograms(accountCode));
      });
    }
  }, [accountCode, dispatch]);

  // Track screen width for responsive design
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 992px)');
    const handleChange = (e) => setIsNarrowScreen(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleChange = (e) => setIsMobile(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const handleTabClick = (tab) => {
    if (isMobile) {
      history.push(`/dashboard/${tab}`);
      return;
    }
    if (tab === 'calendars') {
      history.push(
        lastCalendarsPath?.includes('/dashboard/calendars')
          ? lastCalendarsPath
          : '/dashboard/calendars',
      );
      return;
    }
    if (tab === 'programs') {
      history.push(
        lastProgramsPath?.includes('/dashboard/programs')
          ? lastProgramsPath
          : '/dashboard/programs',
      );
      return;
    }
    history.push(
      lastAthletesPath?.includes('/dashboard/athletes')
        ? lastAthletesPath
        : '/dashboard/athletes',
    );
  };

  const getActiveTab = () => {
    if (location.pathname.includes('/dashboard/calendars')) return 1;
    if (location.pathname.includes('/dashboard/programs')) return 2;
    return 0;
  };
  const activeTab = getActiveTab();
  const isManageAthletesPage = location.pathname === '/dashboard/manage-athletes';

  const renderMobileHomeTab = ({
    key,
    name,
    subtitle,
    icon,
  }) => (
    <TabButtonWrapper key={key}>
      <MobileTabCard type='button' onClick={() => handleTabClick(key)}>
        <MobileTabCardCenter>
          <Icon icon={icon} size={32} color={accountPrimaryColor} />
          <Text styledTextProps={{ fontSize: '18px', fontWeight: 700, letterSpacing: '0.3px' }}>
            {name}
          </Text>
          <Text styledTextProps={{ fontSize: '16px', fontWeight: 400, letterSpacing: '0.3px' }}>
            {subtitle}
          </Text>
        </MobileTabCardCenter>
        <MobileTabArrow>
          <svg
            width='9.412'
            height='37.416'
            viewBox='0 0 9.412 37.416'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            aria-hidden='true'
            focusable='false'
            role='presentation'
          >
            <path d='M1 1 L8.412 18.708 L1 36.416' stroke={accountPrimaryColor} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
          </svg>
        </MobileTabArrow>
      </MobileTabCard>
    </TabButtonWrapper>
  );

  const renderTabButton = ({
    key,
    name,
    subtitle,
    desktopHeight,
  }, index) => (
    <TabButtonWrapper key={key}>
      <Button
        name={name}
        subtitle={isNarrowScreen ? undefined : subtitle}
        onClick={() => handleTabClick(key)}
        large
        fullWidth
        customBorderRadius='8px'
        borderColor={activeTab === index && accountPrimaryColor ? accountPrimaryColor : '#EEE'}
        bgColor={activeTab === index && accountPrimaryColor ? accountPrimaryBg : 'ffffff'}
        fontSize='18px'
        styledTextProps={{ fontWeight: 700, letterSpacing: '0.3px' }}
        styledSubtitleProps={{
          fontSize: '16px',
          color: '#000',
          fontWeight: 400,
          letterSpacing: '0.3px',
        }}
        styledWrapperProps={{
          padding: isNarrowScreen ? '12px 8px' : '16px 24px',
          height: isNarrowScreen ? '90px' : desktopHeight,
          borderWidth: activeTab === index && accountPrimaryColor ? '2px' : '1px',
          gap: '6px',
        }}
      />
    </TabButtonWrapper>
  );

  return (
    <Layout fullWidth>
      <Helmet>
        <title>Home | TeamBuildr</title>
      </Helmet>
      <Container>
        {!isManageAthletesPage && (
        <NavSection isNarrow={isNarrowScreen}>
          {TAB_CONFIG.map(renderTabButton)}
        </NavSection>
        )}
        <ContentSection>
          <Switch>
            <Route exact path='/dashboard'>
              {isMobile ? (
                <MobileHomeContainer>
                  <Text styledTextProps={{ fontSize: '19px', fontWeight: 700 }}>
                    Welcome to TeamBuildr, Coach
                  </Text>
                  {TAB_CONFIG.map(renderMobileHomeTab)}
                </MobileHomeContainer>
              ) : (
                <Redirect to={lastPath || '/dashboard/athletes'} />
              )}
            </Route>
            <Route exact path='/dashboard/manage-athletes'>
              <ManageAthletes
                backPath={lastAthletesPath || '/dashboard/athletes'}
                onEditAthlete={openEditAthleteModal}
              />
            </Route>
            <Route path='/dashboard/athletes/:athleteId?'>
              {({ match }) => (
                <AthleteNavigator
                  athleteId={match?.params?.athleteId}
                  onEditAthlete={openEditAthleteModal}
                  manageUsers={manageUsers}
                  isMobile={isMobile}
                />
              )}
            </Route>
            <Route path='/dashboard/calendars/:calendarId?'>
              {({ match }) => (
                <CalendarNavigator
                  accountCode={accountCode}
                  calendarId={match?.params?.calendarId}
                  manageUsers={manageUsers}
                  isMobile={isMobile}
                />
              )}
            </Route>
            <Route path='/dashboard/programs/:programId?'>
              {({ match }) => (
                <ProgramNavigator
                  accountCode={accountCode}
                  programId={match?.params?.programId}
                  manageUsers={manageUsers}
                  isMobile={isMobile}
                />
              )}
            </Route>
          </Switch>
        </ContentSection>
        <EditAthleteModal
          isOpen={isEditAthleteModalOpen}
          onClose={closeEditAthleteModal}
          athlete={athlete}
          calendars={calendars}
          programs={programs}
          groups={groups}
          initialTab={initialTab}
          addProgramMode={addProgramMode}
        />
        <PreviewCarousel />
      </Container>
    </Layout>
  );
};

export default Navigator;
